import React, { useState, useEffect } from 'react';
import { useAuth } from './SimpleAuthContext';
import { Bell } from 'lucide-react';
import { Badge } from './components/ui/badge';
import axios from 'axios';

const NotificationBell = () => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);

  // Ensure API URL always ends with /api
  const getApiUrl = () => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    return baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
  };
  const API = getApiUrl();

  useEffect(() => {
    if (currentUser) {
      fetchNotifications();
      // Check for new notifications every 5 minutes
      const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  const fetchNotifications = async () => {
    try {
      const token = await currentUser.getIdToken();
      const response = await axios.get(`${API}/notifications/unread`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'trial_expiring':
        return '🎯';
      case 'payment_due':
        return '💳';
      default:
        return '🔔';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'trial_expiring':
        return 'bg-blue-50 border-blue-200';
      case 'payment_due':
        return 'bg-yellow-50 border-yellow-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  if (!currentUser) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
      >
        <Bell className="h-5 w-5 text-slate-600" />
        {unreadCount > 0 && (
          <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-600 text-white text-xs">
            {unreadCount}
          </Badge>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-slate-200 z-50 max-h-96 overflow-y-auto">
          <div className="p-3 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900">Notifications</h3>
            {unreadCount > 0 && (
              <p className="text-xs text-slate-600">{unreadCount} unread</p>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="p-4 text-center text-slate-500 text-sm">
              No new notifications
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {notifications.map((notification, index) => (
                <div
                  key={index}
                  className={`p-3 hover:bg-slate-50 cursor-pointer ${getNotificationColor(notification.type)}`}
                  onClick={() => {
                    if (notification.action_url) {
                      window.location.href = notification.action_url;
                    }
                  }}
                >
                  <div className="flex items-start space-x-2">
                    <span className="text-lg">{getNotificationIcon(notification.type)}</span>
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-slate-900">
                        {notification.title}
                      </p>
                      <p className="text-xs text-slate-600 mt-1">
                        {notification.message}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(notification.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
